/**
 * The Chinese zodiac: the animal, element and polarity of a lunar year.
 *
 * A zodiac year starts on Chinese New Year, the first day of the first month of the Chinese
 * calendar. The month that contains the winter solstice is always the eleventh, which puts
 * the new year at the second new moon after the solstice (occasionally the third), as set
 * out in Reingold and Dershowitz, *Calendrical Calculations: The Ultimate Edition*
 * (Cambridge University Press, 2018). The date falls between 21 January and 20 February,
 * so a birth in January or February can belong to the previous animal year.
 *
 * The dates for 1900–2101 are stored as offsets from 21 January in `NEW_YEAR_OFFSETS`, one
 * base-36 digit per year. The table started from the Chinese calendar in the ICU library
 * built into browsers (astronomical, Beijing time). Every date was then checked against an
 * independent calculation of the new moon (Meeus, *Astronomical Algorithms*, chapter 49) and
 * against published tables. Three years — 1954, 2027 and 2030 — have a new moon within ten
 * minutes of Beijing midnight, where ICU is a day out, and are set to the published dates
 * (3 February 1954, 6 February 2027, 3 February 2030). Keeping a table also makes the page
 * independent of whether a reader's browser ships the Chinese calendar.
 *
 * The 60-year cycle pairs ten heavenly stems with twelve earthly branches. Year 4 CE was
 * Jiǎ-Zǐ, the first of the cycle, so the stem is (year − 4) mod 10 and the branch (year − 4)
 * mod 12. Each pair of stems shares an element (wood, fire, earth, metal, water) and the
 * stems alternate yang and yin.
 */

import { mod, rdFromGregorian } from "@/lib/tool-kit/calendars/rd";

export const FIRST_ZODIAC_YEAR = 1900;
export const LAST_ZODIAC_YEAR = 2100;

/** One base-36 digit per Gregorian year from 1900 to 2101: days after 21 January. */
const NEW_YEAR_OFFSETS =
  "ati8qe4nc1k9sg5od2lbui7qf3nc2k9rg5oe3lati6pf4nc1k8rg6od3masi7pf4nc0j9rg6pd2lash7qf4ncuj8rg6pe2kath7qf3mb1j8sh5od2kati7qf4mb1k8rg5nd2lati7pe3mb1k9rg5oc2lbti7pe3mc0j8rf5od2lath6pf3mc1j8rg5od3k9sh6pf4mb0j8";

export const ANIMALS = [
  "rat",
  "ox",
  "tiger",
  "rabbit",
  "dragon",
  "snake",
  "horse",
  "goat",
  "monkey",
  "rooster",
  "dog",
  "pig",
] as const;
export type Animal = (typeof ANIMALS)[number];

export const ELEMENTS = ["wood", "fire", "earth", "metal", "water"] as const;
export type Element = (typeof ELEMENTS)[number];

/** Stem and branch names in pinyin and Chinese characters (data, not prose). */
export const STEMS = [
  ["Jiǎ", "甲"],
  ["Yǐ", "乙"],
  ["Bǐng", "丙"],
  ["Dīng", "丁"],
  ["Wù", "戊"],
  ["Jǐ", "己"],
  ["Gēng", "庚"],
  ["Xīn", "辛"],
  ["Rén", "壬"],
  ["Guǐ", "癸"],
] as const;

export const BRANCHES = [
  ["Zǐ", "子"],
  ["Chǒu", "丑"],
  ["Yín", "寅"],
  ["Mǎo", "卯"],
  ["Chén", "辰"],
  ["Sì", "巳"],
  ["Wǔ", "午"],
  ["Wèi", "未"],
  ["Shēn", "申"],
  ["Yǒu", "酉"],
  ["Xū", "戌"],
  ["Hài", "亥"],
] as const;

/** R.D. of Chinese New Year in a Gregorian year from 1900 to 2101. */
export function chineseNewYearRd(gregorianYear: number): number | null {
  const digit = NEW_YEAR_OFFSETS[gregorianYear - FIRST_ZODIAC_YEAR];
  if (digit === undefined) {
    return null;
  }
  return rdFromGregorian(gregorianYear, 1, 21) + Number.parseInt(digit, 36);
}

/** The zodiac year (named for the Gregorian year it starts in) that contains an R.D. */
export function zodiacYearOfRd(
  rd: number,
  gregorianYear: number
): number | null {
  const start = chineseNewYearRd(gregorianYear);
  if (start === null) {
    return null;
  }
  return rd >= start ? gregorianYear : gregorianYear - 1;
}

export interface ZodiacYear {
  year: number;
  animal: Animal;
  animalIndex: number;
  element: Element;
  /** True for a yang year (even stem: jiǎ, bǐng, wù, gēng, rén). */
  yang: boolean;
  stem: number;
  branch: number;
  /** 1 to 60: the place in the sexagenary cycle. */
  cyclePosition: number;
}

export function zodiacOfYear(year: number): ZodiacYear {
  const offset = year - 4;
  const stem = mod(offset, 10);
  const branch = mod(offset, 12);
  return {
    year,
    animal: ANIMALS[branch] ?? "rat",
    animalIndex: branch,
    element: ELEMENTS[Math.floor(stem / 2)] ?? "wood",
    yang: stem % 2 === 0,
    stem,
    branch,
    // The pair (stem, branch) determines the position by the Chinese remainder theorem.
    cyclePosition: mod(offset, 60) + 1,
  };
}

/** Years with a given animal in a range of years, oldest first. */
export function yearsOfAnimal(
  animalIndex: number,
  from: number,
  to: number
): number[] {
  const years: number[] = [];
  for (let year = from; year <= to; year++) {
    if (mod(year - 4, 12) === animalIndex) {
      years.push(year);
    }
  }
  return years;
}
